import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from "react-toastify";
import BalanceStatisticOne from '../child/BalanceStatisticOne';

const DashboardDeptDetailsModal = ({ show, handleClose, province }) => {
  const [departmentData, setDepartmentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("tabular");
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Stable function using useCallback
  const fetchDepartmentData = useCallback(async (provinceName) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const deptResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/department-summary`,
        { province: provinceName },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (deptResponse.data.status) {
        setDepartmentData(deptResponse.data.data);
      } else {
        showToast("Failed to fetch department data.", "error");
      }
      
    } catch (error) {
      console.error("Error fetching department data:", error);
      showToast("Failed to fetch department data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]); // Only recreate if API_BASE_URL changes

  // Now include fetchDepartmentData in deps
  useEffect(() => {
    if (show && province) {
      fetchDepartmentData(province);
    }
  }, [show, province, fetchDepartmentData]);

  const showToast = (message, type = "success") => {
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    };

    switch (type) {
      case "success":
        toast.success(message, toastOptions);
        break;
      case "error":
        toast.error(message, toastOptions);
        break;
      default:
        toast(message, toastOptions);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
      <Modal.Header closeButton className="bg-light">
        <img
          src={process.env.PUBLIC_URL + "/assets/images/govpklogo.png"}
          alt="Government of Pakistan Logo"
          className="mx-20 my-4"
          style={{ width: "90px", height: "auto" }}
        />
        <Modal.Title className="h5">
          Department Summary {province && `- ${province}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul
          className="nav button-tab nav-pills mb-16"
          id="pills-tab-four"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10 ${activeTab === "tabular" ? "active" : ""}`}
              id="pills-button-icon-home-tab"
              type="button"
              role="tab"
              aria-controls="pills-button-icon-home"
              aria-selected={activeTab === "tabular"}
              onClick={() => setActiveTab("tabular")}
            >
              <Icon
                icon="solar:home-smile-angle-outline"
                className="text-xl"
              />
              <span className="line-height-1">Tabular View</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10 ${activeTab === "departmental" ? "active" : ""}`}
              id="pills-button-icon-details-tab"
              type="button"
              role="tab"
              aria-controls="pills-button-icon-details"
              aria-selected={activeTab === "departmental"}
              onClick={() => setActiveTab("departmental")}
            >
              <Icon
                icon="hugeicons:folder-details"
                className="text-xl"
              />
              <span className="line-height-1">Graphical View</span>
            </button>
          </li>
        </ul>

        <div className="tab-content" id="pills-tabContent">
          {activeTab === "tabular" ? (
            loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th style={{ textAlign: 'center' }}>Sr.No.</th>
                      <th style={{ textAlign: 'center' }}>Department Name</th>
                      <th style={{ textAlign: 'center' }}>Total Users</th>
                      <th style={{ textAlign: 'center' }}>Total Activities</th>
                      <th style={{ textAlign: 'center' }}>Total Plants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentData.length > 0 ? (
                      departmentData.map((department, index) => (
                        <tr key={index}>
                          <td style={{ textAlign: 'center' }}>{index + 1}</td>
                          <td>
                            <h6 className="text-md mb-0 fw-normal">
                              {department.departmentName || "Unknown Department"}
                            </h6>
                          </td>
                          <td style={{ textAlign: 'center' }}>{department.totalUsers}</td>
                          <td style={{ textAlign: 'center' }}>{department.totalActivities}</td>
                          <td style={{ textAlign: 'center' }}>{department.totalPlants.toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="d-flex flex-column align-items-center">
                            <i className="mdi mdi-database-off text-muted fs-1 mb-2"></i>
                            <p className="text-muted">
                              {province ? `No department data available for ${province}` : 'No department data available'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )
          ) : (
            <BalanceStatisticOne 
              departmentData={departmentData} 
              loading={loading} 
              province={province}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => fetchDepartmentData(province)} 
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DashboardDeptDetailsModal;